import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Languages, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function GoogleButton({ label }) {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={handleGoogle}
      data-testid="google-login-btn"
      className="w-full h-12 rounded-xl border border-border bg-white font-medium flex items-center justify-center gap-3 hover:bg-secondary transition-colors"
    >
      <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-5 w-5" />
      {label}
    </button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-center bg-primary text-white p-14">
        <Languages size={48} />
        <h1 className="font-head font-extrabold text-4xl mt-6 leading-tight">{t("appName")}</h1>
        <p className="text-white/80 text-lg mt-4 max-w-md">{t("heroSub")}</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h2 className="font-head font-bold text-2xl text-foreground">{t("welcome")}</h2>
          <p className="text-muted-foreground mt-1 mb-6">{t("login")}</p>

          <GoogleButton label={t("continueGoogle")} />
          <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground uppercase tracking-wider">
            <span className="h-px bg-border flex-1" /> {t("or")} <span className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("email")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="h-12 mt-1.5" />
            </div>
            <div>
              <Label>{t("password")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="h-12 mt-1.5" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-12 rounded-xl bg-accent hover:bg-accent/90 text-base">
              {loading ? <Loader2 className="animate-spin" /> : t("login")}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {t("noAccount")} <Link to="/register" className="text-accent font-semibold" data-testid="to-register">{t("register")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
